import { Fragment, useMemo, useState, useCallback } from "react";
import { Box, Typography, List, Avatar, IconButton, Stack, Button, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ICarrinhoItem } from "../..";
import { IItensPorLoja } from "../../CarrinhoCard";
import { formatarValorMonetario } from "../../../../../../../../helpers/masks";
import DialogReceituario from "./DialogReceituario";

export default function ListaItens({
    itensPerloja,
    onRemoverItem,
    onEditarItem,
    onEditItensCarrinho
}: ListaItensProps) {

    const [openDialogReceita, setOpenDialogReceita] = useState(false);

    const itensComReceitaSemInfo = useMemo(() => {
        return itensPerloja.flatMap(loja =>
            loja.itens.filter(item =>
                item.emite_receita === 'S' &&
                (!item.cultura || !item.praga)
            )
        );
    }, [itensPerloja]);

    const precisaReceituario = useCallback((itemId: string) => {
        return itensComReceitaSemInfo.some(item => item.id === itemId);
    }, [itensComReceitaSemInfo]);

    const handleEdit = (item: ICarrinhoItem) => {
        onEditarItem(item);
    };

    const handleSaveReceituario = (data: IReceituarioData) => {
        if (data && data.itens) {
            const itensAtualizados: ICarrinhoItem[] = [];

            data.itens.forEach((itemData: IReceituarioItem) => {
                const itemToUpdate = itensComReceitaSemInfo.find(item => item.id === itemData.item_id);
                if (itemToUpdate) {
                    const updatedItem = {
                        ...itemToUpdate,
                        cultura: {
                            id: itemData.cultura_id,
                            nome: itemData.nome_cultura
                        },
                        praga: {
                            id: itemData.praga_id,
                            nome: itemData.nome_cientifico
                        }
                    };

                    itensAtualizados.push(updatedItem);
                }
            });

            if (itensAtualizados.length > 0) {
                onEditItensCarrinho(itensAtualizados);
            }
        }
    };

    return (
        <List>
            {itensComReceitaSemInfo.length > 0 && (
                <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2
                    }}
                    action={
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => setOpenDialogReceita(true)}
                        >
                            Informar
                        </Button>
                    }
                >
                    Este pedido contém {itensComReceitaSemInfo.length} {itensComReceitaSemInfo.length > 1 ? 'itens' : 'item'} que necessita(m) de receituário. Favor informar a cultura e praga.
                </Alert>
            )}

            {itensPerloja.map((item, index) => (
                <Fragment key={index}>
                    <Typography mb={1} variant="subtitle2">
                        Estoque: {item.loja.id} - {item.loja.nome}
                    </Typography>
                    {item.itens.map((item, index) => (
                        <Stack
                            key={index}
                            p={1}
                            mb={2}
                            direction={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            sx={{
                                borderRadius: 1,
                                border: precisaReceituario(item.id)
                                    ? "1px solid #ed6c02"
                                    : "1px solid #e0e0e0",
                            }}
                        >
                            <Box display={"flex"} alignItems={"center"}>
                                <Avatar
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        marginRight: 2,
                                    }}
                                    alt={item.nome}
                                    src={item.imagem?.url || "/imgs/imagem-invalida.png"}
                                />
                                <Box>
                                    <Typography sx={{ fontWeight: "bold" }}>
                                       {item.id} - {item.nome}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.quantidade} x (
                                        {formatarValorMonetario(item.preco)} -{" "}
                                        {formatarValorMonetario(item.desconto ?? 0)}) ={" "}
                                        {formatarValorMonetario(
                                            item.quantidade *
                                            (item.preco - (item.desconto ?? 0))
                                        )}
                                    </Typography>
                                    {item.setor?.maximo_parcelas && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            Parcelamento máx.: {item.setor.maximo_parcelas}x
                                        </Typography>
                                    )}
                                    {item.cultura && item.praga && (
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            color="warning.main"
                                            sx={{ opacity: 0.8 }}
                                        >
                                            Cultura: {item.cultura.nome} | Praga: {item.praga.nome}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box>
                                <IconButton
                                    size="small"
                                    onClick={() => handleEdit(item)}
                                >
                                    <EditIcon fontSize="inherit" />
                                </IconButton>

                                <IconButton
                                    size="small"
                                    onClick={() => onRemoverItem(item.id, item.loja.id)}
                                >
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Stack>
                    ))}
                </Fragment>
            ))}

            {openDialogReceita && (
                <DialogReceituario
                    open={openDialogReceita}
                    handleClose={() => setOpenDialogReceita(false)}
                    itensComReceita={itensComReceitaSemInfo}
                    onSave={handleSaveReceituario}
                />
            )}
        </List>
    );
}

interface ListaItensProps {
    itensPerloja: IItensPorLoja[];
    onRemoverItem: (id: string, loja_id: number) => void;
    onEditarItem: (item: ICarrinhoItem) => void;
    onEditItensCarrinho: (item: ICarrinhoItem[]) => void;
}

export interface IReceituarioData {
    itens: IReceituarioItem[];
}

export interface IReceituarioItem {
    item_id: string;
    cultura_id: number;
    nome_cultura: string;
    praga_id: number;
    nome_cientifico: string;
}

